import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useCallActivityTraces } from '../../hooks/useCallActivityTraces';
import { CallActivityLogPanel } from './CallActivityLogPanel';
import styles from './CallHistoryDrawer.module.css';

interface CallHistoryDrawerProps {
  onClose: () => void;
}

export function CallHistoryButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('activityLog');

  return (
    <button
      type="button"
      className={styles.historyButton}
      onClick={onClick}
      aria-label={t('openButton')}
      title={t('openButton')}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
        <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
      </svg>
    </button>
  );
}

export function CallHistoryDrawer({ onClose }: CallHistoryDrawerProps) {
  const { t } = useTranslation(['activityLog', 'common']);
  const traces = useCallActivityTraces({ enabled: true });
  const { refresh, isLoading } = traces;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} role="presentation" />
      <aside
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="call-history-title"
      >
        <div className={styles.drawerHeader}>
          <div className={styles.drawerHeading}>
            <h2 id="call-history-title" className={styles.drawerTitle}>
              {t('activityLog:title')}
            </h2>
            <p className={styles.drawerSubtitle}>{t('activityLog:subtitle')}</p>
          </div>
          <div className={styles.drawerHeaderActions}>
            <button
              type="button"
              className={`${styles.headerIconButton} ${isLoading ? styles.headerIconButtonSpinning : ''}`}
              onClick={refresh}
              disabled={isLoading}
              aria-label={t('activityLog:refresh')}
              title={t('activityLog:refresh')}
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
              </svg>
            </button>
            <button
              type="button"
              className={styles.headerIconButton}
              onClick={onClose}
              aria-label={t('common:buttons.close')}
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
        </div>
        <div className={styles.drawerBody}>
          <CallActivityLogPanel traces={traces} />
        </div>
      </aside>
    </>,
    document.body
  );
}
